import React, {useReducer, useContext, useCallback} from "react";
import aws from "../libs/awsLib";
import _ from "lodash";


const mergeArrays = (c, i) => _.unionWith(c, i, _.isEqual);

export const DataContext = React.createContext({});
export const MethodsContext = React.createContext({});

export const withData = Component => props => {
  const dataContext = useContext(DataContext);
  const methodsContext = useContext(MethodsContext);
  return <Component {...props} {...dataContext} {...methodsContext}/>;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setUser':
      return {...state, user: action.payload};

    case 'setTheme':
      return {...state, theme: action.payload};

    case 'setBusy':
      return {...state, isBusy: action.payload};

    case 'setUsers':
      return {...state, users: action.payload};

    case 'setMeta':
      return {...state, meta: action.payload};

    case 'setDeliveries':
      return {...state, deliveries: action.payload};

    case 'setSlots':
      return {...state, slots: action.payload};

    case 'setEvents':
      return {...state, events: action.payload};

    case 'addUser':
      return {...state, users: mergeArrays(state.users, action.payload)};

    case 'addMeta':
      return {...state, meta: mergeArrays(state.meta, action.payload)};

    case 'addDeliveries':
      return {...state, deliveries: mergeArrays(state.deliveries, action.payload)};

    case 'addEvents':
      return {...state, events: mergeArrays(state.events, action.payload)};

    case 'addSlots':
      return {...state, slots: mergeArrays(state.slots, action.payload)};

    case 'updateMeta':
      return {
        ...state,
        meta: [
          ...state.meta.filter(item => item._id !== action.payload._id),
          action.payload
        ]
      };

    case 'updateDelivery':
      return {
        ...state,
        deliveries: [
          ...state.deliveries.filter(
            item => item.timestamp !== action.payload.timestamp || item._id !== action.payload._id
          ),
          action.payload
        ]
      };

    case 'updateSlot':
      return {
        ...state,
        slots: [
          ...state.slots.filter(
            item => item.SK !== action.payload.SK || item._id !== action.payload._id
          ),
          action.payload
        ]
      };

    case 'setSlotEvent': {
      const slot = state.slots.filter(
        item => item.timestamp === action.payload.timestamp && item._id === action.payload._id
      )[0];
      slot.status = 'event'

      return {
        ...state,
        slots: [
          ...state.slots.filter(
            item => item.timestamp !== action.payload.timestamp || item._id !== action.payload._id
          ),
          slot
        ]
      };

    }
    case 'updateEvent':
      return {
        ...state,
        events: [
          ...state.events.filter(
            item => item.timestamp !== action.payload.timestamp || item._id !== action.payload._id
          ),
          action.payload
        ]
      };

    case 'deleteUser':
      return {
        ...state,
        users: state.users.filter(item => item._id !== action.payload._id
        )
      };

    case 'deleteMeta':
      return {
        ...state,
        meta: state.meta.filter(item => item._id !== action.payload._id
        )
      };

    case 'deleteDelivery':
      return {
        ...state,
        deliveries: state.deliveries.filter(
          item => item.timestamp !== action.payload.timestamp || item._id !== action.payload._id
        )
      };

    case 'deleteEvent':
      return {
        ...state,
        events: state.events.filter(
          item => item.timestamp !== action.payload.timestamp || item._id !== action.payload._id
        )
      };

    case 'deleteSlot':
      return {
        ...state,
        slots: state.slots.filter(
          item => item.SK !== action.payload.SK || item._id !== action.payload._id
        )
      };

    default:
      return state;
  }
};

const DataContainer = props => {
  const [data, dispatch] = useReducer(reducer, {
    deliveries: [],
    users: [],
    meta: [],
    events: [],
    user: {},
    slots: [],
    theme: 'light',
    isBusy: false
  });

  const alertError = func => async params => {
    try {
      return await func(params);
    } catch (e) {
      e.response && alert(e.response.data);
      throw e;
    }
  };

  const busyFetch = func => async params => {
    dispatch({type: 'setBusy', payload: true});
    const rep = await func(params);
    dispatch({type: 'setBusy', payload: false});
    return rep
  };

  const setUser = useCallback(busyFetch(alertError(user =>
    dispatch({type: 'setUser', payload: user}))),
    []
  );

  const setTheme = useCallback(busyFetch(alertError(theme =>
    dispatch({type: 'setTheme', payload: theme}))),
    []
  );

  const fetchUsers = useCallback(busyFetch(alertError(async () => {
    const data = await aws.scanUsers();
    const dataTransform = _.map(data, el => ({...el, name: el.userName}));
    dispatch({type: 'setUsers', payload: dataTransform});
    return data;
  })), []);

  const fetchMeta = useCallback(busyFetch(alertError(async () => {
    const meta = await aws.scanMeta();
    dispatch({type: 'setMeta', payload: meta});
    return meta;
  })), []);

  const fetchMetaById = useCallback(busyFetch(alertError(async _id => {
    const meta = await aws.queryMetaById(_id);
    dispatch({type: 'setMeta', payload: meta});
    return meta;
  })), []);

  const fetchDeliveriesByDate = useCallback(busyFetch(alertError(async ({startDate, endDate}) => {
    const deliveries = await aws.queryDeliveriesByDate(startDate, endDate);
    dispatch({type: 'setDeliveries', payload: deliveries});
    return deliveries;
  })), []);

  const setSlots = useCallback(busyFetch(alertError(async (slots) => {
    dispatch({type: 'setSlots', payload: slots});
    return slots;
  })), []);

  const fetchSlotByIdAndSK = useCallback(busyFetch(alertError(async ({_id, SK}) => {
    const slots = await aws.querySlotByIdAndSK(_id, SK);
    dispatch({type: 'setSlots', payload: slots});
    return slots;
  })), []);

  const fetchSlotsByDate = useCallback(busyFetch(alertError(async ({startDate, endDate}) => {
    const slots = await aws.querySlotsByDate(startDate, endDate);
    dispatch({type: 'setSlots', payload: slots});
    return slots;
  })), []);

  const fetchEventsByDate = useCallback(busyFetch(alertError(async ({startDate, endDate}) => {
    const events = await aws.queryEventsByDate(startDate, endDate);
    dispatch({type: 'setEvents', payload: events});
    return events;
  })), []);

  const fetchDeliveriesById = useCallback(busyFetch(alertError(async ({_id}) => {
    const deliveries = await aws.queryDeliveriesByIdAndDate(_id);
    dispatch({type: 'setDeliveries', payload: deliveries});
    return deliveries;
  })), []);

  const fetchEventsByIdAndDate = useCallback(busyFetch(alertError(async ({_id, startDate, endDate}) => {
    const events = await aws.queryEventsByIdAndDate(_id, startDate, endDate);
    dispatch({type: 'setEvents', payload: events});
    return events;
  })), []);

  const createMeta = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createMeta(data);
    dispatch({type: 'addMeta', payload: [rep]});
    return rep;
  })), []);

  const createUser = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createUser(data);
    const repTransform = {...rep, name: rep.userName}
    dispatch({type: 'addUser', payload: [repTransform]});
    return rep;
  })), []);

  const createDelivery = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createDelivery(data);
    dispatch({type: 'addDeliveries', payload: [rep]});
    return rep;
  })), []);

  const createTransfer = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createTransfer(data);
    dispatch({type: 'addEvents', payload: [rep]});
    return rep;
  })), []);

  const createEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createEvent(data);
    dispatch({type: 'setSlotEvent', payload: rep});
    dispatch({type: 'addEvents', payload: [rep]});
    return rep;
  })), []);

  const createPausedEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createPausedEvent(data);
    dispatch({type: 'addEvents', payload: rep});
    return rep;
  })), []);

  const createChooseEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createChooseEvent(data);
    dispatch({type: 'addEvents', payload: rep});
    return rep;
  })), []);

  const createUnavailableEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.createUnavailableEvent(data);
    dispatch({type: 'addEvents', payload: rep});
    return rep;
  })), []);

  const chooseVehicle = useCallback(busyFetch(alertError(async data => {
    return await aws.chooseVehicle(data);
  })), []);

  const updateMeta = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateMeta(data);
    dispatch({type: 'updateMeta', payload: rep});
    return rep;
  })), []);

  const updateDelivery = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateDelivery(data);
    dispatch({type: 'updateDelivery', payload: rep});
    return rep;
  })), []);

  const updateDeliveryStatus = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateDeliveryStatus(data);
    dispatch({type: 'updateDelivery', payload: rep});
    return rep;
  })), []);

  const updateDeliveryVehicle = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateDeliveryVehicle(data);
    dispatch({type: 'updateDelivery', payload: rep});
    return rep;
  })), []);

  const updateSlot = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateSlot(data);
    dispatch({type: 'updateSlot', payload: rep});
    return rep;
  })), []);

  const updateEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.updateEvent(data);
    dispatch({type: 'updateEvent', payload: rep});
    return rep;
  })), []);

  const deleteUser = useCallback(busyFetch(alertError(async _id => {
    const rep = await aws.deleteUser(_id);
    dispatch({type: 'deleteUser', payload: _id});
    return rep;
  })), []);

  const deleteMeta = useCallback(busyFetch(alertError(async _id => {
    const rep = await aws.deleteMeta(_id);
    dispatch({type: 'deleteMeta', payload: _id});
    return rep;
  })), []);

  const deleteDelivery = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.deleteDelivery(data);
    dispatch({type: 'deleteDelivery', payload: data});
    return rep;
  })), []);

  const deleteEvent = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.deleteEvent(data);
    dispatch({type: 'deleteEvent', payload: data});
    return rep;
  })), []);

  const deleteSlot = useCallback(busyFetch(alertError(async data => {
    const rep = await aws.deleteSlot(data);
    dispatch({type: 'deleteSlot', payload: data});
    return rep;
  })), []);

  return <DataContext.Provider value={{data}}>
    <MethodsContext.Provider value={{
      setUser,
      chooseVehicle,
      fetchUsers,
      fetchMeta,
      fetchMetaById,
      fetchDeliveriesByDate,
      fetchDeliveriesById,
      fetchEventsByDate,
      fetchSlotByIdAndSK,
      fetchSlotsByDate,
      setSlots,
      fetchEventsByIdAndDate,
      createUser,
      createMeta,
      createDelivery,
      createEvent,
      createTransfer,
      createChooseEvent,
      createPausedEvent,
      createUnavailableEvent,
      updateMeta,
      updateDelivery,
      updateDeliveryStatus,
      updateDeliveryVehicle,
      updateSlot,
      updateEvent,
      deleteUser,
      deleteMeta,
      deleteDelivery,
      deleteEvent,
      deleteSlot,
      setTheme
    }}>
      {props.children}
    </MethodsContext.Provider>
  </DataContext.Provider>;
};

export default DataContainer;



// const updateDeliveryStatus = useCallback(busyFetch(async deliveryData => {
//   const rep = await aws.updateDeliveryStatus(deliveryData);
//   dispatch({type: 'updateDelivery', payload: {...rep, ...deliveryData}})
// }), []);
// 
